import { strToU8, zipSync } from 'fflate'

/**
 * Gemeinsames für den **Export** (#252): Texte, Datum ohne Sprache, Dateinamen, ZIP. Alles rein — die
 * Unterseite reicht Daten und Übersetzung hinein.
 *
 * Beschreibungen, Notizen, Kommentare und das „Warum“ eines Ziels sind Klartext (Textfelder); HTML
 * (ADR 0029) hält nur der Pinnwand-„Text“ (`kind: 'text'`).
 */

export type Translate = (key: string, options?: Record<string, unknown>) => string

const ENTITIES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', '#39': "'", apos: "'", nbsp: ' ' }
const decode = (text: string) => text.replace(/&(amp|lt|gt|quot|#39|apos|nbsp);/g, (_, name: string) => ENTITIES[name] ?? '')

/** Ein Klartext-Feld, wie es eingegeben wurde — nur Windows-Zeilenenden werden `\n`. */
export function plainText(text: string | null | undefined): string {
  return (text ?? '').replace(/\r\n?/g, '\n').trim()
}

/** Klartext mit Absätzen: Blöcke und Zeilenumbrüche werden Zeilen, alles andere fällt weg. */
export function htmlToPlainText(html: string | null | undefined): string {
  return decode((html ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
    .replace(/<[^>]+>/g, ''))
    .split('\n')
    .map(line => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line, index, lines) => line !== '' || (index > 0 && lines[index - 1] !== ''))
    .join('\n')
    .trim()
}

/**
 * Markdown aus dem HTML einer Beschreibung — das, was der Editor schreibt: Absätze, Umbrüche, fett,
 * kursiv, durchgestrichen, Code, Links, Listen, Überschriften, Zitate. Unbekanntes wird Text.
 */
export function htmlToMarkdown(html: string | null | undefined): string {
  if (!html) return ''
  let text = html
    .replace(/\r?\n/g, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<(strong|b)>(.*?)<\/\1>/gi, '**$2**')
    .replace(/<(em|i)>(.*?)<\/\1>/gi, '*$2*')
    .replace(/<(s|del|strike)>(.*?)<\/\1>/gi, '~~$2~~')
    .replace(/<code>(.*?)<\/code>/gi, '`$1`')
    .replace(/<a\s[^>]*href="([^"]*)"[^>]*>(.*?)<\/a>/gi, (_, href: string, label: string) => `[${label}](${href})`)
    .replace(/<h([1-6])[^>]*>(.*?)<\/h\1>/gi, (_, level: string, body: string) => `\n${'#'.repeat(Math.min(6, Number(level) + 2))} ${body}\n`)
    .replace(/<blockquote[^>]*>(.*?)<\/blockquote>/gi, (_, body: string) => `\n> ${body.replace(/<\/p>\s*<p[^>]*>/gi, '\n> ')}\n`)
  // Listen: Der Editor packt jeden Punkt in einen Absatz — der fällt weg, sonst stünde je Punkt eine Leerzeile.
  text = text.replace(/<li([^>]*)>\s*<p[^>]*>(.*?)<\/p>\s*<\/li>/gi, '<li$1>$2</li>')
  // Geordnete nummerieren, ungeordnete mit Strich.
  text = text.replace(/<ol[^>]*>(.*?)<\/ol>/gi, (_, body: string) => {
    let n = 0
    return `\n${body.replace(/<li[^>]*>(.*?)<\/li>/gi, (_m: string, item: string) => `${++n}. ${item}\n`)}\n`
  })
  text = text.replace(/<ul[^>]*>(.*?)<\/ul>/gi, (_, body: string) => `\n${body.replace(/<li[^>]*>(.*?)<\/li>/gi, (_m: string, item: string) => `- ${item}\n`)}\n`)
  text = text
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
  return decode(text)
    .split('\n')
    .map(line => line.replace(/[ \t]+$/g, ''))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

const pad = (n: number) => String(n).padStart(2, '0')

/** `JJJJ-MM-TT` nach dem lokalen Tag — andere Apps lesen das unabhängig von der Sprache. */
export function exportDate(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return ''
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/** `JJJJ-MM-TT HH:mm` nach der lokalen Zeit. */
export function exportDateTime(date: Date | null | undefined): string {
  if (!date || Number.isNaN(date.getTime())) return ''
  return `${exportDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Windows entpackt diese Namen nicht, auch nicht mit Endung („Con.md“).
const WINDOWS_RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\.|$)/i

/** Ein Teil eines Pfads in der ZIP-Datei: keine Pfadzeichen, nicht leer, nicht endlos lang, unter Windows entpackbar. */
export function fileNamePart(name: string, fallback: string): string {
  const clean = [...name]
    .map(char => (char.charCodeAt(0) < 32 || '\\/:*?"<>|'.includes(char) ? '-' : char))
    .join('')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\.+/, '')
    .slice(0, 80)
    // Punkte und Leerzeichen am Ende streicht Windows still — ein Ordner „Ideen...“ verlöre sonst seinen Namen.
    .replace(/[. ]+$/, '')
  if (!clean) return fallback
  return WINDOWS_RESERVED.test(clean) ? `_${clean}` : clean
}

/** Vergibt eindeutige Pfade: Gibt es `Arbeit/Steuer.md` schon, heißt der nächste `Arbeit/Steuer (2).md`. */
export function uniquePaths() {
  const taken = new Set<string>()
  return (path: string): string => {
    const dot = path.lastIndexOf('.')
    const [base, ext] = dot > path.lastIndexOf('/') ? [path.slice(0, dot), path.slice(dot)] : [path, '']
    let candidate = path
    for (let n = 2; taken.has(candidate.toLowerCase()); n++) candidate = `${base} (${n})${ext}`
    taken.add(candidate.toLowerCase())
    return candidate
  }
}

/** Packt die Dateien eines Exports in eine ZIP-Datei. */
export function zipExport(files: Record<string, string>): Uint8Array {
  return zipSync(Object.fromEntries(Object.entries(files).map(([path, content]) => [path, strToU8(content)])))
}

/** `HappyTask-Export-CSV-JJJJ-MM-TT.zip` bzw. `…-Markdown-…` — sprachunabhängig. */
export function exportFileName(format: 'csv' | 'markdown', date: Date = new Date()): string {
  return `HappyTask-Export-${format === 'csv' ? 'CSV' : 'Markdown'}-${exportDate(date)}.zip`
}
